import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  applyThemeToDocument,
  readStoredTheme,
  isValidThemeId,
  isValidMode,
} from '@/theme/themeConstants';
import { fetchAuthMe } from '@/utils/authMe';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = 'terrakronos_session_token';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => readStoredTheme().themeId);
  const [mode, setMode] = useState(() => readStoredTheme().mode);

  // On load: if logged in, prefer server-stored UI prefs over localStorage
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    let cancelled = false;
    fetchAuthMe()
      .then((res) => {
        if (cancelled) return;
        const u = res.data;
        if (isValidThemeId(u.ui_theme) && isValidMode(u.ui_theme_mode)) {
          setThemeId(u.ui_theme);
          setMode(u.ui_theme_mode);
          applyThemeToDocument(u.ui_theme, u.ui_theme_mode);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyThemeToDocument(themeId, mode);
  }, [themeId, mode]);

  const setTheme = useCallback(async (nextThemeId, nextMode) => {
    const t = isValidThemeId(nextThemeId) ? nextThemeId : 'default';
    const m = isValidMode(nextMode) ? nextMode : 'dark';
    setThemeId(t);
    setMode(m);
    applyThemeToDocument(t, m);

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      try {
        await axios.patch(
          `${API}/auth/me/ui-preferences`,
          { ui_theme: t, ui_theme_mode: m },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.warn('Could not sync UI theme to server', e);
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      themeId,
      mode,
      setTheme,
    }),
    [themeId, mode, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

export function useThemeOptional() {
  return useContext(ThemeContext);
}
